import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Scale, Users, Globe, GraduationCap } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/20595719/pexels-photo-20595719.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const chapters = [
  { n: "01", title: "Academisch", icon: GraduationCap, body: "Moot courts, lezingen en workshops die je juridische kennis verdiepen ver buiten het auditorium." },
  { n: "02", title: "Netwerk", icon: Users, body: "Ontmoet advocaten, magistraten en medestudenten op events die deuren openen." },
  { n: "03", title: "Internationaal", icon: Globe, body: "Deel van een netwerk van 375+ universiteiten in 44 landen, met stages via STEP." },
  { n: "04", title: "Waarden", icon: Scale, body: "Een rechtvaardige wereld waarin respect voor menselijke waardigheid en culturele diversiteit centraal staat." },
];

export default function Home() {
  const { config } = useSiteConfig();
  const [gallery, setGallery] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.45, 0.75]);

  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] overflow-hidden" style={{ background: "var(--elsa-blue)" }}>
        <motion.div className="absolute inset-0" style={{ y, scale }}>
          <img src={HERO_IMG} alt="Justitiepaleis Brussel" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div className="absolute inset-0" style={{ background: `rgba(10,48,135,${0.6})`, opacity: overlay }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A3087] via-transparent to-[#0A3087]/40" />

        <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col justify-end pb-20">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-white/70 text-sm tracking-[0.3em] uppercase mb-6"
          >
            European Law Students' Association · Brussel
          </motion.p>
          <h1 className="font-serif text-white text-[13vw] md:text-[8vw] leading-[0.86] tracking-tight">
            <MaskedLines lines={[config.hero_title_line1, config.hero_title_line2]} delay={0.3} />
          </h1>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mt-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
              className="text-white/85 text-lg max-w-md text-balance"
            >
              {config.hero_subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}
              className="flex gap-4"
            >
              <Link to="/lid-worden" data-testid="hero-join-btn" className="group inline-flex items-center gap-2 text-sm font-600 px-6 py-3.5 rounded-full text-white transition-transform hover:-translate-y-0.5" style={{ background: "var(--elsa-blue)" }}>
                Lid worden <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/events" className="inline-flex items-center gap-2 text-sm font-600 px-6 py-3.5 rounded-full text-white border border-white/40 hover:bg-white/10 transition-colors">
                Bekijk events
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="py-5 border-y border-black/10" style={{ background: "#EEF1F7" }}>
        <Marquee speed={40} gradient={false}>
          <div className="marquee-track flex items-center">
            {Array.from({ length: 6 }).map((_, i) => (
              <span key={i} className="font-serif text-3xl md:text-4xl mx-8 flex items-center gap-8" style={{ color: "var(--elsa-blue)" }}>
                Academisch · Netwerken · Internationaal · Vriendschap
                <span className="h-2 w-2 rounded-full" style={{ background: "var(--elsa-blue)" }} />
              </span>
            ))}
          </div>
        </Marquee>
      </div>

      {/* INTRO */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-28">
        <div className="grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-3">
            <p className="text-sm tracking-[0.25em] uppercase" style={{ color: "var(--elsa-grey)" }}>Wie zijn wij</p>
          </Reveal>
          <div className="lg:col-span-9">
            <Reveal>
              <p className="font-serif text-3xl md:text-5xl leading-[1.15] tracking-tight text-[#0B0B0C]">
                {config.intro_text}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="py-10 pb-28" style={{ background: "#F5F5F6" }}>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal><h2 className="font-serif text-4xl md:text-5xl mb-14 text-[#0B0B0C]">Waar wij voor staan</h2></Reveal>
          <div className="grid md:grid-cols-2 gap-px bg-black/10 border border-black/10">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="group bg-[#F9F9F6] p-8 md:p-12 h-full transition-colors hover:bg-white">
                  <div className="flex items-start justify-between mb-8">
                    <span className="font-serif text-6xl md:text-7xl text-[#0A3087]/15 group-hover:text-[#0A3087]/40 transition-colors">{c.n}</span>
                    <c.icon size={28} strokeWidth={1.5} style={{ color: "var(--elsa-blue)" }} />
                  </div>
                  <h3 className="font-serif text-3xl mb-3 text-[#0B0B0C]">{c.title}</h3>
                  <p className="text-muted-foreground text-balance">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY / AMBIANCE */}
      <section className="py-10 pb-28 overflow-hidden" style={{ background: "#F5F5F6" }}>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <Reveal className="lg:col-span-7">
              <p className="text-sm tracking-[0.25em] uppercase mb-4" style={{ color: "var(--elsa-grey)" }}>ELSA in beeld</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0B0B0C] leading-[1.05]">Momenten uit onze community.</h2>
            </Reveal>
            <Reveal className="lg:col-span-5">
              <p className="text-muted-foreground text-balance">Van netwerkavonden tot moot courts — een blik op het verenigingsleven van ELSA Brussel.</p>
            </Reveal>
          </div>
        </div>
        {gallery.length > 0 && (
          <div data-testid="home-gallery-carousel">
            <Marquee speed={45} gradient={false} pauseOnHover>
              {gallery.map((g, i) => (
                <figure key={g.id || i} className="relative mx-3 h-[300px] w-[240px] md:h-[420px] md:w-[340px] overflow-hidden rounded-lg bg-[#EEF1F7]">
                  <img src={g.image_url} alt={g.caption || "ELSA Brussel community"} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                  {g.caption && (
                    <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A3087]/80 to-transparent p-4 text-sm text-white">{g.caption}</figcaption>
                  )}
                </figure>
              ))}
            </Marquee>
          </div>
        )}
      </section>

      {/* STATS */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-28">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[["44", "Landen"], ["375+", "Universiteiten"], ["50K+", "Leden wereldwijd"], ["25+", "Events per jaar"]].map(([n, l], i) => (
            <Reveal key={l} delay={i * 0.08}>
              <div className="border-l-2 pl-5" style={{ borderColor: "var(--elsa-blue)" }}>
                <p className="font-serif text-5xl md:text-6xl text-[#0B0B0C]">{n}</p>
                <p className="text-muted-foreground mt-1">{l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
